/**
 * Compress an image File to a JPEG data URL <= maxKB (default 400KB).
 * Resizes to fit within maxDim (default 1280px) on the longer side,
 * then iteratively lowers quality until size target is met.
 * Returns a data URL string (e.g. "data:image/jpeg;base64,...").
 */
export async function compressImage(file, { maxKB = 400, maxDim = 1280 } = {}) {
  if (!file) return "";
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image files are supported");
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Failed to decode image"));
    i.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  let { width: w, height: h } = img;
  const ratio = Math.min(maxDim / w, maxDim / h, 1);
  w = Math.round(w * ratio);
  h = Math.round(h * ratio);
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  const maxBytes = maxKB * 1024;
  let quality = 0.92;
  let out = canvas.toDataURL("image/jpeg", quality);
  // Each step decreases quality by 0.1 until under target or floor reached.
  while (estimateBytes(out) > maxBytes && quality > 0.2) {
    quality = Math.max(0.2, quality - 0.1);
    out = canvas.toDataURL("image/jpeg", quality);
  }
  // If still too big, downscale further in 0.8x steps.
  while (estimateBytes(out) > maxBytes && Math.min(w, h) > 256) {
    w = Math.round(w * 0.8);
    h = Math.round(h * 0.8);
    canvas.width = w;
    canvas.height = h;
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    out = canvas.toDataURL("image/jpeg", quality);
  }
  return out;
}

function estimateBytes(dataUrl) {
  // strip "data:image/jpeg;base64," header then compute byte length from base64 length
  const i = dataUrl.indexOf(",");
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
}
